export type WebEventPayload = {
  items?: unknown[];
  dims?: Record<string, unknown>;
  extra?: Record<string, unknown>;
};

const HOME_PAGE_PATHS = new Set([
  "/",
  "/news",
  "/videos",
  "/shorts",
  "/news-stack",
  "/publishers",
]);
const DYNAMIC_ROUTE_SEGMENT_PATTERN = /\[[^\]]+\]/;
const LOCALE_PATH_PREFIX_PATTERN = /^\/(?:en|ms|zh)(?=\/|$)/;

declare global {
  interface Window {
    web_event?: {
      track: (event: string, action: string, payload?: WebEventPayload) => void;
    };
  }
}

export function trackPageView(pathname: string, queryParams: string) {
  if (typeof window === "undefined") return;

  const resolvedPath = resolveTrackingPath(pathname);
  const payload = getPageProps(resolvedPath, queryParams);

  if (isManuallyHandledPage(resolvedPath)) {
    /// Page tracking is handled separately
    return;
  } else if (isSearchPage(resolvedPath)) {
    trackArticleSearchEvent(payload);
  } else {
    trackPageViewEvent(payload);
  }
}

export function logNewsEvent(id: string, topics: string[], publisher: string) {
  const payload = {
    items: [
      {
        type: "article",
        id: `LUMI_${id}`,
      },
    ],
    extra: {
      page_type: "article",
      page_cate: topics,
      publisher_name: publisher,
    },
  };

  trackPageViewEvent(payload);
}

export function logNewsStackEvent(title: string) {
  const payload = {
    extra: {
      page_type: "stack",
      page_cate: title,
    },
  };

  trackPageViewEvent(payload);
}

export function logPublisherEvent(publisher: string) {
  const payload = {
    extra: {
      page_type: "publisher",
      publisher_name: publisher,
    },
  };
  trackPageViewEvent(payload);
}

export function logTopicEvent(topicName: string) {
  const payload = {
    extra: {
      page_type: "category",
      page_cate: topicName,
    },
  };

  trackPageViewEvent(payload);
}

export function logShareEvent(id: string, shareType: string) {
  const payload = {
    items: [
      {
        type: "article",
        id: `LUMI_${id}`,
      },
    ],
    extra: {
      identify_event: "share_article",
      share_type: shareType,
    },
  };
  trackArticleShareEvent(payload);
}

function logTrackingEvent(
  event: string,
  action: string,
  payload: WebEventPayload
) {
  console.log(`${event}, ${action}`);
  console.log(payload);
  window.web_event?.track(event, action, payload);
}

/// Tracks pageview event
function trackPageViewEvent(payload: WebEventPayload) {
  logTrackingEvent("pageview", "view", payload);
}

/// Tracks article search event
function trackArticleSearchEvent(payload: WebEventPayload) {
  logTrackingEvent("article", "search", payload);
}

/// Tracks article share  event
function trackArticleShareEvent(payload: WebEventPayload) {
  logTrackingEvent("article", "share", payload);
}

/// Strips the locale out of the route,
// and cleans route if router returns route name instead of route path
function resolveTrackingPath(pathname: string): string {
  const normalizedInput = sanitizeLocalePath(pathname);

  // Fallback to real browser path if a route pattern leaks in (e.g. /videos/[id]).
  if (DYNAMIC_ROUTE_SEGMENT_PATTERN.test(normalizedInput)) {
    return sanitizeLocalePath(window.location.pathname);
  }

  return normalizedInput;
}

/// Removes the locale prefix pattern
function sanitizeLocalePath(url: string): string {
  const path = url.split(/[?#]/)[0] || "/";
  const sanitized = path.replace(LOCALE_PATH_PREFIX_PATTERN, "");
  return sanitized || "/";
}

/// Checks if the given path name belongs to a landing page
function isHomePage(pathname: string) {
  return HOME_PAGE_PATHS.has(pathname);
}

/// Determines if the current page requires manual tracking handling
function isManuallyHandledPage(pathname: string) {
  return (
    isNewsArticlePage(pathname) ||
    isNewsStackPage(pathname) ||
    isTopicPage(pathname) ||
    isPublisherPage(pathname)
  );
}

/// Determines if the current page is an article (video/shorts) page
function isArticlePage(pathname: string) {
  return pathname.startsWith("/videos/") || pathname.startsWith("/shorts/");
}

/// Determines if the current page is a news article page
export function isNewsArticlePage(pathname: string) {
  return pathname.startsWith("/news/");
}

/// Determines if the current page is a topic's landing page
export function isTopicPage(pathname: string) {
  return pathname.startsWith("/topics/");
}

/// Determines if the current page is a news stack's page
export function isNewsStackPage(pathname: string) {
  return pathname.startsWith("/news-stack/");
}

/// Determines if the current page is a publisher's landing page
export function isPublisherPage(pathname: string) {
  return pathname.startsWith("/publishers/");
}

/// Determines if the current page is the search page
function isSearchPage(pathname: string) {
  return pathname.startsWith("/explore");
}

/// Gets all the path segments
function getPathSegments(pathname: string) {
  return pathname.split("/").filter(Boolean);
}

/// Gets the current search query param
function getSearchParam(queryParams: string) {
  const params = new URLSearchParams(queryParams);
  return params.get("search");
}

/// Generates the page's payload based on the current page path
function getPageProps(pathname: string, queryParams: string): WebEventPayload {
  /// Any of the landing pages. News, videos, publishers, shorts etc.
  if (isHomePage(pathname)) {
    const cate = pathname === "/" ? "homepage" : pathname.replace("/", "");
    return {
      extra: {
        page_type: "homepage",
        page_cate: cate,
      },
    };
  }

  const segments = getPathSegments(pathname);
  const routeName = segments[0] ?? "";
  const pageId = segments[segments.length - 1] ?? "";

  /// Article pages = video/shorts. Not news, since news is manually handled
  if (isArticlePage(pathname)) {
    return {
      items: [
        {
          page_type: "article",
          id: `LUMI_${pageId}`,
        },
      ],
      extra: {
        page_type: "article",
        page_cate: routeName,
      },
    };
  }
  /// Pages with subroutes
  else if (segments.length > 1) {
    return {
      extra: {
        page_type: routeName,
        page_cate: pageId,
      },
    };
  }
  /// Search pages with search param
  else if (isSearchPage(pathname)) {
    const searchParam = getSearchParam(queryParams);
    if (searchParam) {
      return {
        extra: { src_search_term: searchParam },
      };
    }
  }
  /// Other pages
  else {
    return {
      extra: {
        page_type: routeName,
        page_cate: "other",
      },
    };
  }
}
